import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  product_interest: "",
  quantity: "",
  destination_country: "",
  message: "",
};

export default function ContactForm({ compact = false }) {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Inquiry submitted. Our team will respond within 24 hours.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please email us directly at aadityaforbusiness@gmail.com");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full px-4 py-3 bg-white border border-neutral-300 rounded-md text-brand-charcoal text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold transition";

  return (
    <form
      data-testid="contact-form"
      onSubmit={submit}
      className="bg-white rounded-lg p-6 md:p-8 shadow-md border border-neutral-200 space-y-5"
    >
      <div className={`grid ${compact ? "" : "md:grid-cols-2"} gap-5`}>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-brand-charcoal">Full Name *</label>
          <input data-testid="contact-input-name" required value={form.name} onChange={update("name")} className={`${inputCls} mt-1.5`} placeholder="Your full name" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-brand-charcoal">Company</label>
          <input data-testid="contact-input-company" value={form.company} onChange={update("company")} className={`${inputCls} mt-1.5`} placeholder="Your company name" />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-brand-charcoal">Email *</label>
          <input data-testid="contact-input-email" required type="email" value={form.email} onChange={update("email")} className={`${inputCls} mt-1.5`} placeholder="you@company.com" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-brand-charcoal">Phone</label>
          <input data-testid="contact-input-phone" value={form.phone} onChange={update("phone")} className={`${inputCls} mt-1.5`} placeholder="+1 555 000 0000" />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-brand-charcoal">Product Interest</label>
          <select data-testid="contact-select-product" value={form.product_interest} onChange={update("product_interest")} className={`${inputCls} mt-1.5`}>
            <option value="">Select product</option>
            <option>Ashwagandha Extract</option>
            <option>Bacopa Extract (Brahmi)</option>
            <option>Ginger Extract</option>
            <option>Turmeric / Curcumin Extract</option>
            <option>Spices (Pepper, Cumin, etc.)</option>
            <option>Rice (Basmati & Non-Basmati)</option>
            <option>Coffee — Arabica</option>
            <option>Coffee — Robusta</option>
            <option>Eco Packaging (Cups / Lids / Straws)</option>
            <option>Other / Custom</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-brand-charcoal">Quantity Required</label>
          <input data-testid="contact-input-quantity" value={form.quantity} onChange={update("quantity")} className={`${inputCls} mt-1.5`} placeholder="e.g., 25 kg trial / 500 kg" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-brand-charcoal">Destination Country</label>
          <input data-testid="contact-input-country" value={form.destination_country} onChange={update("destination_country")} className={`${inputCls} mt-1.5`} placeholder="e.g., Germany" />
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-brand-charcoal">Message *</label>
        <textarea data-testid="contact-textarea-message" required value={form.message} onChange={update("message")} rows={5} className={`${inputCls} mt-1.5 resize-none`} placeholder="Tell us about your requirement, specifications, timeline, etc." />
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="contact-submit-button"
        className="btn-gold w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? "Submitting..." : (
          <>
            <Send className="w-4 h-4" />
            Submit Inquiry
          </>
        )}
      </button>

      <p className="text-xs text-neutral-500 text-center">
        We respond to all B2B inquiries within one business day. Your information is confidential.
      </p>
    </form>
  );
}
